import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  Activity, RefreshCw, CheckCircle2, AlertTriangle, XCircle, Database,
} from 'lucide-react';
import { Button } from '../../components/ui/button';

import { API_BASE as API } from '../../config';

const STATUS_STYLES = {
  ok:   { icon: CheckCircle2, color: 'text-emerald-400', bg: 'bg-emerald-500/10', border: 'border-emerald-500/30', dot: 'bg-emerald-400' },
  warn: { icon: AlertTriangle, color: 'text-amber-400',  bg: 'bg-amber-500/10',   border: 'border-amber-500/30',  dot: 'bg-amber-400' },
  fail: { icon: XCircle,      color: 'text-rose-400',   bg: 'bg-rose-500/10',    border: 'border-rose-500/30',   dot: 'bg-rose-400' },
};

function OverallBanner({ status, checkedAt }) {
  const s = STATUS_STYLES[status] || STATUS_STYLES.warn;
  const Icon = s.icon;
  const label = { ok: 'All systems healthy', warn: 'Operational with warnings', fail: 'Critical issues detected' }[status] || status;
  return (
    <div className={`p-4 rounded-lg border ${s.bg} ${s.border} flex items-center justify-between gap-3`} data-testid="overall-banner">
      <div className="flex items-center gap-3">
        <Icon className={`w-6 h-6 ${s.color}`} />
        <div>
          <h2 className={`text-lg font-bold ${s.color}`}>{label}</h2>
          <p className="text-xs text-slate-500">Last checked {new Date(checkedAt).toLocaleString()}</p>
        </div>
      </div>
    </div>
  );
}

function ProbeRow({ probe }) {
  const s = STATUS_STYLES[probe.status] || STATUS_STYLES.warn;
  return (
    <tr className="border-t border-slate-800 hover:bg-slate-800/40" data-testid={`probe-${probe.name.toLowerCase().replace(/\s+/g, '-').replace(/[^\w-]/g, '')}`}>
      <td className="px-4 py-2.5 whitespace-nowrap">
        <span className={`inline-flex items-center gap-2 ${s.color}`}>
          <span className={`w-2 h-2 rounded-full ${s.dot}`} />
          {probe.name}
        </span>
      </td>
      <td className="px-4 py-2.5 text-xs uppercase font-semibold tracking-wider">
        <span className={`px-2 py-0.5 rounded ${s.bg} ${s.color}`}>{probe.status}</span>
      </td>
      <td className="px-4 py-2.5 text-slate-300 text-xs">{probe.detail}</td>
    </tr>
  );
}

export default function DevSystemHealth() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/dev/system-health`, { withCredentials: true });
      setData(res.data);
    } catch {
      setData(null);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  if (loading && !data) return <div className="text-center py-12 text-slate-400">Running health checks…</div>;
  if (!data) return <div className="text-center py-12 text-slate-400">No data</div>;

  return (
    <div className="text-slate-200">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <Activity className="w-6 h-6 text-emerald-400" />
          <div>
            <h1 className="text-2xl font-bold text-white">System Health</h1>
            <p className="text-sm text-slate-400">Backend probes for operator monitoring</p>
          </div>
        </div>
        <Button variant="outline" size="sm" onClick={fetchData} disabled={loading}
          className="border-slate-700 bg-slate-900 text-slate-200 hover:bg-slate-800" data-testid="refresh-health">
          <RefreshCw className={`w-3.5 h-3.5 mr-1.5 ${loading ? 'animate-spin' : ''}`} />
          Re-run checks
        </Button>
      </div>

      <div className="mb-6">
        <OverallBanner status={data.overall} checkedAt={data.checked_at} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 bg-slate-900 border border-slate-800 rounded-lg overflow-hidden">
          <div className="px-4 py-3 border-b border-slate-800">
            <h3 className="text-sm font-semibold text-white">Probes ({data.probes.length})</h3>
          </div>
          <table className="w-full text-sm">
            <thead className="bg-slate-950 text-slate-400 text-[11px] uppercase tracking-wider">
              <tr>
                <th className="text-left px-4 py-2 font-medium">Check</th>
                <th className="text-left px-4 py-2 font-medium">Status</th>
                <th className="text-left px-4 py-2 font-medium">Detail</th>
              </tr>
            </thead>
            <tbody>
              {data.probes.map((p, i) => <ProbeRow key={i} probe={p} />)}
            </tbody>
          </table>
        </div>

        <div className="bg-slate-900 border border-slate-800 rounded-lg">
          <div className="px-4 py-3 border-b border-slate-800 flex items-center gap-2">
            <Database className="w-3.5 h-3.5 text-emerald-400" />
            <h3 className="text-sm font-semibold text-white">Collection sizes</h3>
          </div>
          <div className="p-4 space-y-2">
            {(data.collections || []).map(c => (
              <div key={c.name} className="flex items-center justify-between text-xs">
                <span className="text-slate-300 font-mono">{c.name}</span>
                <span className="text-white font-semibold">{c.count.toLocaleString()}</span>
              </div>
            ))}
            {!data.collections?.length && <p className="text-xs text-slate-500">No data</p>}
          </div>
        </div>
      </div>
    </div>
  );
}
